import { inject, Injectable } from "@angular/core";
import { VendorApi } from "../Api/VendorApi";
import { VendorState } from "./VendorState";
import { VendorSearchRequest } from "../Dto/VendorSearchRequest";
import { CreateVendorRequest } from "../Dto/CreateVendorRequest";
import { Vendor } from "../Dto/VendorModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class VendorEffects {
  private readonly vendorApi = inject(VendorApi);
  private readonly vendorState = inject(VendorState);

  async searchVendors(params: VendorSearchRequest = {}): Promise<void> {
    const merged = { page: 1, limit: 10, ...params };
    this.vendorState.setLoading(true);
    this.vendorState.setError(null);

    try {
      const response = await this.vendorApi.searchVendors(merged);
      this.vendorState.setVendors(response.data, response.pagination);
      this.vendorState.setSearchParams(merged);
    } catch (exception: unknown) {
      this.vendorState.setError(getBackendErrorMessage(exception));
    } finally {
      this.vendorState.setLoading(false);
    }
  }

  async createVendor(payload: CreateVendorRequest): Promise<Vendor> {
    try {
      const created = await this.vendorApi.createVendor(payload);
      this.vendorState.addVendor(created);
      return created;
    } catch (exception: unknown) {
      this.vendorState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async updateVendor(id: string, payload: CreateVendorRequest): Promise<Vendor> {
    try {
      const updated = await this.vendorApi.updateVendor(id, payload);
      this.vendorState.updateVendor(updated);
      return updated;
    } catch (exception: unknown) {
      this.vendorState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async getVendorById(id: string): Promise<Vendor | null> {
    try {
      return await this.vendorApi.getVendorById(id);
    } catch {
      return null;
    }
  }

  async deleteVendor(id: string): Promise<boolean> {
    try {
      await this.vendorApi.deleteVendor(id);
      this.vendorState.removeVendor(id);
      return true;
    } catch (exception: unknown) {
      this.vendorState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }
}
